import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import UserMenu from './UserMenu';
import ApiService from '../services/api';
import './ApplicationDetail.css';

const ApplicationDetail = ({ user, onSignOut }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const [availableInterviewers, setAvailableInterviewers] = useState([]);
  const [selectedInterviewer, setSelectedInterviewer] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [removingInterviewer, setRemovingInterviewer] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [finalizeData, setFinalizeData] = useState({
    status: '',
    start_date: '',
    end_date: '',
    location: 'Cambridge',
    scholarship_percent: 0,
    final_fee: '',
    onboarding_contact_name: '',
    onboarding_contact_email: ''
  });
  const [notification, setNotification] = useState(null);
  const [questionnaireResponses, setQuestionnaireResponses] = useState(null);
  const [questionnaireLoading, setQuestionnaireLoading] = useState(false);
  const [interviewQuestions, setInterviewQuestions] = useState(null);
  const [interviewQuestionsLoading, setInterviewQuestionsLoading] = useState(false);
  const [editingInterviewQuestions, setEditingInterviewQuestions] = useState(false);
  const [interviewQuestionsData, setInterviewQuestionsData] = useState({
    challenges: '',
    contribution: '',
    referral: '',
    teamSize: ''
  });
  const [editingDecision, setEditingDecision] = useState(false);
  const [selectedDecision, setSelectedDecision] = useState('');
  const [isSuperUser, setIsSuperUser] = useState(false);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000); // Auto-hide after 5 seconds
  };

  const hideNotification = () => {
    setNotification(null);
  };

  const handleDownloadPitchDeck = async () => {
    // Handle legacy powerpointUrl (direct link)
    if (!currentApplication.powerpointS3Key && currentApplication.powerpointUrl) {
      window.open(currentApplication.powerpointUrl, '_blank');
      return;
    }
    
    if (!currentApplication.powerpointS3Key) return;
    
    try {
      const downloadUrl = ApiService.generateSignedDownloadUrl(currentApplication.powerpointS3Key);
      const headers = await ApiService.getAuthHeaders();
      
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: headers
      });

      if (response.ok) {
        // Parse the JSON response to get the download URL
        const data = await response.json();
        if (data.downloadUrl) {
          // Fetch the actual file from the presigned URL
          const fileResponse = await fetch(data.downloadUrl);
          if (fileResponse.ok) {
            const blob = await fileResponse.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = data.filename || currentApplication.pitchDeckName || 'pitch-deck.pdf';
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
          } else {
            showNotification('Failed to download file from S3', 'error');
          }
        } else {
          showNotification('Failed to get download URL', 'error');
        }
      } else {
        showNotification('Failed to download pitch deck', 'error');
      }
    } catch (error) {
      console.error('Error downloading pitch deck:', error);
      showNotification('Error downloading pitch deck', 'error');
    }
  };

  const fetchQuestionnaireResponses = useCallback(async () => {
    if (!id) return;
    
    try {
      setQuestionnaireLoading(true);
      const data = await ApiService.getQuestionnaireResponses(id);
      setQuestionnaireResponses(data.questionnaireResponses);
    } catch (err) {
      console.error('Failed to fetch questionnaire responses:', err);
      // Don't set error state for questionnaire responses since it's optional
    } finally {
      setQuestionnaireLoading(false);
    }
  }, [id]);

  const fetchInterviewQuestions = useCallback(async () => {
    if (!id) return;
    
    try {
      setInterviewQuestionsLoading(true);
      const data = await ApiService.getInterviewQuestions(id);
      setInterviewQuestions(data.interviewQuestions);
      if (data.interviewQuestions) {
        setInterviewQuestionsData({
          challenges: data.interviewQuestions.challenges || '',
          contribution: data.interviewQuestions.contribution || '',
          referral: data.interviewQuestions.referral || '',
          teamSize: data.interviewQuestions.teamSize || ''
        });
      }
    } catch (err) {
      console.error('Failed to fetch interview questions:', err);
      // Don't set error state for interview questions since it's optional
    } finally {
      setInterviewQuestionsLoading(false);
    }
  }, [id]);

  const canEditInterviewQuestions = () => {
    if (!user || !application) return false;
    
    // Don't allow editing if status is finalized
    const finalizedStatuses = ['Accepted', 'Rejected after interview', 'Closed'];
    if (finalizedStatuses.includes(application.status)) return false;
    
    // SuperUsers can always edit, or check if user is assigned interviewer
    if (isSuperUser) return true;
    
    const userEmail = user?.signInDetails?.loginId || user?.username;
    const assignedInterviewers = application.assignedInterviewers || [];
    const assignedInterviewer = application.assignedInterviewer;
    
    // Check if user is in the assigned interviewers list
    return assignedInterviewers.includes(userEmail) || assignedInterviewer === userEmail;
  };

  const shouldShowInterviewQuestions = () => {
    if (!application) return false;
    // Hide interview questions section if status is "Rejected"
    return application.status !== 'Rejected';
  };

  const startEditingInterviewQuestions = () => {
    setEditingInterviewQuestions(true);
  };

  const cancelEditingInterviewQuestions = () => {
    setEditingInterviewQuestions(false);
    // Reset form data to original values
    if (interviewQuestions) {
      setInterviewQuestionsData({
        challenges: interviewQuestions.challenges || '',
        contribution: interviewQuestions.contribution || '',
        referral: interviewQuestions.referral || '',
        teamSize: interviewQuestions.teamSize || ''
      });
    }
  };

  const saveInterviewQuestions = async () => {
    try {
      const data = await ApiService.saveInterviewQuestions(id, interviewQuestionsData);
      setInterviewQuestions(data.interviewQuestions);
      setEditingInterviewQuestions(false);
      showNotification('Interview questions saved successfully');
    } catch (err) {
      console.error('Failed to save interview questions:', err);
      showNotification('Failed to save interview questions', 'error');
    }
  };

  const handleInterviewQuestionChange = (field, value) => {
    setInterviewQuestionsData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const canEditDecision = () => {
    if (!user || !application) return false;
    
    // Don't allow editing if status is finalized
    const finalizedStatuses = ['Accepted', 'Rejected after interview', 'Closed'];
    if (finalizedStatuses.includes(application.status)) return false;
    
    // SuperUsers can always edit, or check if user is assigned interviewer
    if (isSuperUser) return true;
    
    const userEmail = user?.signInDetails?.loginId || user?.username;
    const assignedInterviewers = application.assignedInterviewers || [];
    const assignedInterviewer = application.assignedInterviewer;
    
    // Check if user is in the assigned interviewers list
    return assignedInterviewers.includes(userEmail) || assignedInterviewer === userEmail;
  };

  const startEditingDecision = () => {
    const currentDecision = application?.evaluationResult?.decision || application?.decision || application?.aiAnalysis?.finalDecision || application?.grading?.finalDecision || application?.grading?.decision || '';
    setSelectedDecision(currentDecision);
    setEditingDecision(true);
  };

  const cancelEditingDecision = () => {
    setEditingDecision(false);
    setSelectedDecision('');
  };

  const saveDecision = async () => {
    try {
      const data = await ApiService.updateDecision(id, selectedDecision);
      // Update the application data with the new decision
      setApplication(prev => ({
        ...prev,
        evaluationResult: {
          ...prev.evaluationResult,
          decision: selectedDecision
        },
        decision: selectedDecision,
        aiAnalysis: {
          ...prev.aiAnalysis,
          finalDecision: selectedDecision
        }
      }));
      setEditingDecision(false);
      showNotification('Decision updated successfully');
    } catch (err) {
      console.error('Failed to update decision:', err);
      showNotification('Failed to update decision', 'error');
    }
  };

  const decisionOptions = ['Join Mailing List', 'Station Membership', 'Startup In Residence', 'Close Orbit'];

  const fetchApplication = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ApiService.getApplicationById(id);
      setApplication(data);
    } catch (err) {
      console.error('Failed to fetch application:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchApplication();
    fetchQuestionnaireResponses();
    fetchInterviewQuestions();
    checkSuperUserStatus();
  }, [fetchApplication, fetchQuestionnaireResponses, fetchInterviewQuestions]);

  const checkSuperUserStatus = async () => {
    try {
      const superUserStatus = await ApiService.isSuperUser();
      setIsSuperUser(superUserStatus);
    } catch (err) {
      console.error('Error checking super user status:', err);
      setIsSuperUser(false);
    }
  };

  // Debug logging
  console.log('Debug - canEdit check:', {
    userEmail: user?.signInDetails?.loginId || user?.username,
    assignedInterviewer: application?.assignedInterviewer,
    fullUser: user
  });



  const startAssigning = async () => {
    setIsAssigning(true);
    try {
      // Fetch available interviewers filtered by application site
      const applicationSite = application?.location;
      const interviewers = await ApiService.getAvailableInterviewers(applicationSite);
      setAvailableInterviewers(interviewers);
      setSelectedInterviewer('');
      
      // Show a message if no interviewers are available for this site
      if (interviewers.length === 0 && applicationSite) {
        setError(`No interviewers available for ${applicationSite}. Please add interviewers for this site first.`);
      }
    } catch (err) {
      console.error('Failed to fetch interviewers:', err);
      setError(err.message);
    }
  };

  const cancelAssigning = () => {
    setIsAssigning(false);
    setAvailableInterviewers([]);
    setSelectedInterviewer('');
  };

  const assignInterviewer = async () => {
    if (!selectedInterviewer) return;
    
    try {
      setAssigning(true);
      await ApiService.assignInterviewer(id, selectedInterviewer);
      // Refresh the application data
      await fetchApplication();
      setIsAssigning(false);
      setAvailableInterviewers([]);
      setSelectedInterviewer('');
    } catch (err) {
      console.error('Failed to assign interviewer:', err);
      setError(err.message);
    } finally {
      setAssigning(false);
    }
  };

  const removeInterviewer = async (interviewerEmail) => {
    try {
      setRemovingInterviewer(interviewerEmail);
      await ApiService.removeInterviewer(id, interviewerEmail);
      // Refresh the application data
      await fetchApplication();
    } catch (err) {
      console.error('Failed to remove interviewer:', err);
      setError(err.message);
    } finally {
      setRemovingInterviewer(null);
    }
  };

  const openFinalizeModal = () => {
    setShowFinalizeModal(true);
    setFinalizeData({
      status: '',
      start_date: '',
      end_date: '',
      location: currentApplication?.location || 'Cambridge',
      scholarship_percent: 0,
      final_fee: '',
      onboarding_contact_name: '',
      onboarding_contact_email: ''
    });
  };

  const closeFinalizeModal = () => {
    setShowFinalizeModal(false);
    setFinalizeData({
      status: '',
      start_date: '',
      end_date: '',
      location: currentApplication?.location || 'Cambridge',
      scholarship_percent: 0,
      final_fee: '',
      onboarding_contact_name: '',
      onboarding_contact_email: ''
    });
  };

  const handleFinalizeDataChange = (field, value) => {
    setFinalizeData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const finalizeApplication = async () => {
    if (!finalizeData.status) {
      setError('Please select a status.');
      return;
    }

    try {
      setFinalizing(true);
      const result = await ApiService.finalizeApplication(id, finalizeData);
      // Show success notification
      const emailMessage = result.emailSent ? 'Email sent to applicant.' : 'No email sent.';
      showNotification(`Application finalized! Status: ${result.newStatus}. ${emailMessage}`, 'success');
      // Refresh the application data
      await fetchApplication();
      closeFinalizeModal();
    } catch (err) {
      console.error('Failed to finalize application:', err);
      setError(err.message);
    } finally {
      setFinalizing(false);
    }
  };

  // Dummy fallback data for development
  const dummyApplications = {
    1: {
      id: 1,
      firstName: 'Connor',
      lastName: 'Paladino',
      email: 'connor.p@kredservices.com',
      score: 8.5,
      status: 'Interview Scheduled',
      pitchDeckName: 'Aethos_Pitch_Deck_Connor_Paladino.pptx',
      powerpointUrl: 'https://example.com/powerpoint1.pptx',
      formAnswers: {
        'Company Name': 'TechStart Solutions',
        'Industry': 'FinTech',
        'Stage': 'Seed',
        'Founded': '2023',
        'Team Size': '5-10',
        'Previous Funding': '$250,000',
        'Monthly Revenue': '$15,000',
        'Business Model': 'SaaS subscription with tiered pricing',
        'Problem Statement': 'Small businesses struggle with financial management and lack access to sophisticated tools that larger companies use.',
        'Solution Overview': 'AI-powered financial dashboard that automates bookkeeping, provides real-time insights, and predicts cash flow.',
        'Target Market': 'Small to medium businesses with 1-50 employees, particularly service-based companies',
        'Competitive Advantage': 'Our AI model is specifically trained on SMB financial patterns and integrates with 50+ popular business tools',
        'Growth Strategy': 'Partner with accounting firms and business consultants to reach target customers',
        'Funding Use': 'Product development (60%), marketing (25%), team expansion (15%)'
      }
    },
    2: {
      id: 2,
      firstName: 'Sarah',
      lastName: 'Johnson',
      email: 'sarah.johnson@email.com',
      score: 9.2,
      status: 'Under Review',
      pitchDeckName: 'HealthTech_Pitch_Sarah_Johnson.pptx',
      powerpointUrl: 'https://example.com/powerpoint2.pptx',
      formAnswers: {
        'Company Name': 'MedConnect Pro',
        'Industry': 'HealthTech',
        'Stage': 'Series A',
        'Founded': '2022',
        'Team Size': '15-20',
        'Previous Funding': '$1.2M',
        'Monthly Revenue': '$85,000',
        'Business Model': 'B2B SaaS with per-provider licensing',
        'Problem Statement': 'Healthcare providers spend too much time on administrative tasks instead of patient care.',
        'Solution Overview': 'Integrated platform that streamlines patient scheduling, billing, and electronic health records.',
        'Target Market': 'Medium-sized medical practices with 5-50 healthcare providers',
        'Competitive Advantage': 'HIPAA-compliant with 99.9% uptime and seamless integration with existing systems',
        'Growth Strategy': 'Direct sales to medical practices and partnerships with medical associations',
        'Funding Use': 'Sales team expansion (40%), product development (35%), compliance and security (25%)'
      }
    },
    // Add more dummy data for other applications...
  };

  // Use real data if available, fallback to dummy data, or show not found
  const currentApplication = application || dummyApplications[id];

  // Check if current user is one of the assigned interviewers or a SuperUser
  const userEmail = user?.signInDetails?.loginId || user?.username;
  const assignedInterviewers = currentApplication?.assignedInterviewers || [];
  const legacyInterviewer = currentApplication?.assignedInterviewer;
  const isAssignedInterviewer = userEmail && (assignedInterviewers.includes(userEmail) || legacyInterviewer === userEmail);
  
  // SuperUsers have full access, assigned interviewers have limited access
  const canEdit = isSuperUser || isAssignedInterviewer;
  
  // Use the same logic as canEdit - SuperUsers and assigned interviewers can add new interviewers
  // But also check if status is finalized
  const finalizedStatuses = ['Accepted', 'Rejected after interview', 'Closed'];
  const canAssignInterviewers = canEdit && !finalizedStatuses.includes(currentApplication?.status);
  
  // Debug logging for authorization
  console.log('Debug - Authorization check:', {
    userEmail,
    isSuperUser,
    isAssignedInterviewer,
    assignedInterviewers,
    legacyInterviewer,
    canEdit,
    canAssignInterviewers,
    applicationStatus: currentApplication?.status
  });

  if (loading) {
    return null;
  }

  if (error) {
    return (
      <div className="detail-container">
        <div className="error-panel">
          <h2>Error Loading Application</h2>
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={fetchApplication} className="retry-btn">
              Retry
            </button>
            <button onClick={() => navigate('/dashboard')} className="back-btn">
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentApplication) {
    return (
      <div className="detail-container">
        <div className="error-panel">
          <h2>Application Not Found</h2>
          <p>The requested application could not be found.</p>
          <button onClick={() => navigate('/dashboard')} className="back-btn">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'approved': return '#86efac';
      case 'accepted': return '#22c55e'; // Bright green for accepted
      case 'rejected': return '#9ca3af'; // Blackish gray pastel
      case 'rejected after interview': return '#fca5a5'; // Previous rejected color (light red/pink)
      case 'closed': return '#6b7280'; // Darker gray for closed
      case 'interview scheduled': return '#93c5fd';
      case 'pending interview': return '#fed7aa';
      case 'under review': return '#c4b5fd';
      case 'invited': return '#a7f3d0'; // New status from POC
      case 'denied': return '#fca5a5'; // New status from POC
      case 'pending': return '#fde68a'; // New status from POC
      default: return '#d1d5db';
    }
  };

  const getDecisionClass = (decision) => {
    if (!decision || decision === 'N/A') return '';
    
    // Handle both capitalized and lowercase versions for backward compatibility
    const normalizedDecision = decision.toLowerCase();
    
    const lowInterestDecisions = ['join mailing list'];
    const mediumInterestDecisions = ['station membership'];
    const highInterestDecisions = ['startup in residence', 'close orbit'];
    
    if (lowInterestDecisions.includes(normalizedDecision)) return 'decision-low';
    if (mediumInterestDecisions.includes(normalizedDecision)) return 'decision-medium';
    if (highInterestDecisions.includes(normalizedDecision)) return 'decision-high';
    
    return '';
  };

  return (
    <div className="detail-container">
      {notification && (
        <div className={`notification ${notification.type}`}>
          <div className="notification-content">
            <span className="notification-message">{notification.message}</span>
            <button className="notification-close" onClick={hideNotification}>
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}
      
      <div className="detail-panel">
        <div className="button-row">
          <button onClick={() => navigate('/dashboard')} className="back-btn">
            <svg className="back-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Back to Dashboard
          </button>
          <UserMenu user={user} onSignOut={onSignOut} />
        </div>

        <div className="detail-header">
          <h1 className="detail-title">Application Details</h1>
        </div>

        <div className="detail-content">
          {/* Applicant Info Section */}
          <div className="info-section">
            <div className="section-header">
              <h2 className="section-title">Applicant Information</h2>
              {(() => {
                // Check if user can finalize this application
                const canFinalize = currentApplication.status !== 'Accepted' && currentApplication.status !== 'Rejected after interview' && currentApplication.status !== 'Closed';
                const isChris = userEmail === 'chris@aethos.org';
                const isBerlinApp = currentApplication.location?.toLowerCase() === 'berlin';
                
                // Show button if: (SuperUser) OR (chris@aethos.org AND Berlin app)
                const showFinalizeButton = canFinalize && ((isSuperUser && canEdit) || (isChris && isBerlinApp));
                
                return showFinalizeButton ? (
                  <button onClick={openFinalizeModal} disabled={finalizing} className="finalize-btn">
                    <svg className="finalize-icon" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Finalize Application
                  </button>
                ) : null;
              })()}
            </div>
            <div className="applicant-info-layout">
              {/* Left two-thirds: Application details */}
              <div className="applicant-details">
                <div className="detail-row">
                  <div className="detail-item">
                    <label>Name:</label>
                    <span>{currentApplication.firstName} {currentApplication.lastName}</span>
                  </div>
                  <div className="detail-item">
                    <label>Email:</label>
                    <span>{currentApplication.email}</span>
                  </div>
                </div>
                
                
                <div className="detail-row">
                  <div className="detail-item">
                    <label>Status:</label>
                    <span 
                      className="status-display"
                      style={{ backgroundColor: getStatusColor(currentApplication.status) }}
                    >
                      {currentApplication.status}
                    </span>
                  </div>
                  {currentApplication.createdAt && (
                    <div className="detail-item">
                      <label>Submitted:</label>
                      <span className="timestamp-info">
                        {new Date(currentApplication.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  )}
                </div>

                <div className="detail-row full-width">
                  <div className="detail-item interviewer-assignment">
                    <label>Assigned Interviewers:</label>
                    <div className="interviewer-controls-wrapper">
                      <div className="assigned-list">
                        {(() => {
                          // Combine assignedInterviewers array with legacyInterviewer if it exists
                          const allAssigned = [...assignedInterviewers];
                          if (legacyInterviewer && !assignedInterviewers.includes(legacyInterviewer)) {
                            allAssigned.push(legacyInterviewer);
                          }
                          
                          return allAssigned.length > 0 ? (
                            allAssigned.map((interviewer, index) => (
                              <span key={index} className="interviewer-tag">
                                {interviewer}
                                {canAssignInterviewers && (
                                  <button 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeInterviewer(interviewer);
                                    }}
                                    disabled={removingInterviewer === interviewer}
                                    className="remove-interviewer-btn"
                                    title="Remove interviewer"
                                  >
                                    {removingInterviewer === interviewer ? (
                                      <span className="removing-spinner">⌛</span>
                                    ) : (
                                      <svg viewBox="0 0 20 20" fill="currentColor" className="remove-icon">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                      </svg>
                                    )}
                                  </button>
                                )}
                              </span>
                            ))
                          ) : (
                            <span className="no-interviewers">No interviewers assigned</span>
                          );
                        })()}
                      </div>
                      
                      <div className="interviewer-action">
                        {canAssignInterviewers && !isAssigning ? (
                          <button onClick={startAssigning} className="assign-btn">
                            <svg className="assign-icon" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                            </svg>
                            Add Interviewer
                          </button>
                        ) : canAssignInterviewers && isAssigning ? (
                          <div className="assign-controls">
                            {application?.location && (
                              <div className="site-filter-info">
                                Showing interviewers for: <strong>{application.location}</strong>
                              </div>
                            )}
                            <select 
                              value={selectedInterviewer} 
                              onChange={(e) => setSelectedInterviewer(e.target.value)}
                              className="interviewer-select"
                            >
                              <option value="">Select an interviewer...</option>
                              {availableInterviewers.filter(interviewer => {
                                // Combine assignedInterviewers array with legacyInterviewer
                                const allAssigned = [...assignedInterviewers];
                                if (legacyInterviewer && !assignedInterviewers.includes(legacyInterviewer)) {
                                  allAssigned.push(legacyInterviewer);
                                }
                                return !allAssigned.includes(interviewer.email);
                              }).map((interviewer) => (
                                <option key={interviewer.email} value={interviewer.email}>
                                  {interviewer.email}
                                </option>
                              ))}
                            </select>
                            <button 
                              onClick={assignInterviewer} 
                              disabled={!selectedInterviewer || assigning} 
                              className="save-assign-btn"
                            >
                              {assigning ? 'Adding...' : 'Add'}
                            </button>
                            <button onClick={cancelAssigning} className="cancel-assign-btn">
                              Cancel
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Pitch Deck Section */}
          <div className="pitch-deck-section">
            <h2 className="section-title">Pitch Deck</h2>
            <div className="pitch-deck-item">
              <div className="pitch-deck-info">
                <span className="pitch-deck-name">{currentApplication.pitchDeckName || 'Pitch Deck'}</span>
                <button 
                  onClick={handleDownloadPitchDeck}
                  className="download-link"
                  disabled={!currentApplication.powerpointS3Key && !currentApplication.powerpointUrl}
                >
                  <svg className="download-icon" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Download Pitch Deck
                </button>
              </div>
            </div>
            
            {/* Product Demo Link - only show if questionnaire is completed */}
            {questionnaireResponses?.productDemo && (
              <div className="product-demo-item">
                <div className="product-demo-info">
                  <span className="product-demo-name">Product Demo</span>
                  <a 
                    href={questionnaireResponses.productDemo} 
                    className="download-link demo-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg className="download-icon" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                    </svg>
                    View Product Demo
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* LinkedIn Section */}
          {currentApplication.linkedinUrl && (
            <div className="linkedin-section">
              <h2 className="section-title">LinkedIn Profile</h2>
              <div className="linkedin-item">
                <a 
                  href={currentApplication.linkedinUrl} 
                  className="linkedin-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg className="linkedin-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  View LinkedIn Profile
                </a>
              </div>
            </div>
          )}

          {/* Interview Questions Section - Hidden if status is "Rejected" */}
          {shouldShowInterviewQuestions() && (
          <div className="interview-questions-section">
            <div className="section-header">
              <h2 className="section-title">Interview Questions</h2>
              {canEditInterviewQuestions() && !editingInterviewQuestions && (
                <button onClick={startEditingInterviewQuestions} className="edit-btn">
                  <svg className="edit-icon" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                  Edit
                </button>
              )}
            </div>
            
            {interviewQuestionsLoading ? (
              <div className="interview-questions-loading">Loading interview questions...</div>
            ) : editingInterviewQuestions ? (
              <div className="interview-questions-form">
                <div className="interview-question-item">
                  <label className="interview-question-label">
                    What are the main challenges you are currently facing, and how do you see the Æthos Station could contribute to your project? *
                  </label>
                  <textarea
                    value={interviewQuestionsData.challenges}
                    onChange={(e) => handleInterviewQuestionChange('challenges', e.target.value)}
                    className="interview-question-textarea"
                    rows="4"
                    placeholder="Enter answer..."
                  />
                </div>
                
                <div className="interview-question-item">
                  <label className="interview-question-label">
                    How do you hope to contribute to the Æthos Community, and what value do you expect to receive from it? *
                  </label>
                  <textarea
                    value={interviewQuestionsData.contribution}
                    onChange={(e) => handleInterviewQuestionChange('contribution', e.target.value)}
                    className="interview-question-textarea"
                    rows="4"
                    placeholder="Enter answer..."
                  />
                </div>
                
                <div className="interview-question-item">
                  <label className="interview-question-label">
                    If you were referred by someone, who referred you?
                  </label>
                  <textarea
                    value={interviewQuestionsData.referral}
                    onChange={(e) => handleInterviewQuestionChange('referral', e.target.value)}
                    className="interview-question-textarea"
                    rows="2"
                    placeholder="Enter answer..."
                  />
                </div>
                
                <div className="interview-question-item">
                  <label className="interview-question-label">
                    How many people will be full-time at the Æthos Station? *
                  </label>
                  <textarea
                    value={interviewQuestionsData.teamSize}
                    onChange={(e) => handleInterviewQuestionChange('teamSize', e.target.value)}
                    className="interview-question-textarea"
                    rows="2"
                    placeholder="Enter answer..."
                  />
                </div>
                
                <div className="interview-questions-actions">
                  <button onClick={cancelEditingInterviewQuestions} className="cancel-btn">
                    Cancel
                  </button>
                  <button onClick={saveInterviewQuestions} className="save-btn">
                    Save Answers
                  </button>
                </div>
              </div>
            ) : (
              <div className="interview-questions-display">
                <div className="interview-question-item">
                  <strong>What are the main challenges you are currently facing, and how do you see the Æthos Station could contribute to your project? *</strong>
                  <div className={`interview-answer ${!interviewQuestions?.challenges ? 'empty' : ''}`}>
                    {interviewQuestions?.challenges || 'No answer provided'}
                  </div>
                </div>
                
                <div className="interview-question-item">
                  <strong>How do you hope to contribute to the Æthos Community, and what value do you expect to receive from it? *</strong>
                  <div className={`interview-answer ${!interviewQuestions?.contribution ? 'empty' : ''}`}>
                    {interviewQuestions?.contribution || 'No answer provided'}
                  </div>
                </div>
                
                <div className="interview-question-item">
                  <strong>If you were referred by someone, who referred you?</strong>
                  <div className={`interview-answer ${!interviewQuestions?.referral ? 'empty' : ''}`}>
                    {interviewQuestions?.referral || 'No answer provided'}
                  </div>
                </div>
                
                <div className="interview-question-item">
                  <strong>How many people will be full-time at the Æthos Station? *</strong>
                  <div className={`interview-answer ${!interviewQuestions?.teamSize ? 'empty' : ''}`}>
                    {interviewQuestions?.teamSize || 'No answer provided'}
                  </div>
                </div>
              </div>
            )}
          </div>
          )}

          {/* Questionnaire Responses Section */}
          <div className="questionnaire-section">
            <h2 className="section-title">Questionnaire Answers</h2>
            {questionnaireLoading ? (
              <div className="questionnaire-loading">Loading questionnaire responses...</div>
            ) : questionnaireResponses ? (
              <div className="questionnaire-responses">
                <div className="questionnaire-item">
                  <strong>How did you find out about Æthos?</strong>
                  <span>{questionnaireResponses.howDidYouFind ? 
                    questionnaireResponses.howDidYouFind.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 
                    ''}</span>
                </div>
                <div className="questionnaire-item">
                  <strong>Have you incorporated?</strong>
                  <span>{questionnaireResponses.incorporated ? 
                    questionnaireResponses.incorporated.charAt(0).toUpperCase() + questionnaireResponses.incorporated.slice(1).toLowerCase() : 
                    ''}</span>
                </div>

                <div className="questionnaire-item">
                  <strong>Station applying to:</strong>
                  <span>{currentApplication.location ? 
                    currentApplication.location.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 
                    'Not specified'}</span>
                </div>

                {questionnaireResponses.submittedAt && (
                  <div className="questionnaire-item">
                    <strong>Submitted at:</strong>
                    <span>{new Date(questionnaireResponses.submittedAt).toLocaleString()}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="no-questionnaire">
                <p>No Response Yet</p>
              </div>
            )}
          </div>

          {/* Application Answers Section */}
          <div className="answers-section">
            <h2 className="section-title">Application Info</h2>
            <div className="answers-grid">
              {currentApplication.formAnswers && Array.isArray(currentApplication.formAnswers) && currentApplication.formAnswers.length > 0 ? (
                currentApplication.formAnswers.map((question) => (
                  <div key={question.id} className="answer-item">
                    <label className="question-label">{(question.question || question.category).replace(/\s*\*\s*$/, '')}:</label>
                    <p className="question-description">{question.answer || question.description}</p>
                  </div>
                ))
              ) : (
                <div className="no-answers">
                  <p>No assessment questions available.</p>
                </div>
              )}
            </div>


          </div>
        </div>

        {/* Finalize Application Modal */}
        {showFinalizeModal && (
          <div className="modal-overlay" onClick={closeFinalizeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Finalize Application</h3>
                <button className="modal-close" onClick={closeFinalizeModal}>
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              
              <div className="modal-body">
                <div className="form-group">
                  <label>Final Status:</label>
                  <select 
                    value={finalizeData.status} 
                    onChange={(e) => handleFinalizeDataChange('status', e.target.value)}
                    className="status-select"
                  >
                    <option value="">Select status...</option>
                    <option value="Accepted">Accepted</option>
                    <option value="Rejected after interview">Rejected after interview</option>
                    <option value="Closed">Close (no rejection email)</option>
                  </select>
                </div>
                
                {/* Residency Details Form - Show only when Accepted is selected */}
                {finalizeData.status === 'Accepted' && (
                  <div className="residency-details-form">
                    <h4 style={{margin: '24px 0 16px 0', color: '#1d1d1f', fontSize: '1.1rem', fontWeight: '600'}}>Residency Details</h4>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>Start Date:</label>
                        <input
                          type="date"
                          value={finalizeData.start_date}
                          onChange={(e) => handleFinalizeDataChange('start_date', e.target.value)}
                          className="date-input"
                        />
                      </div>
                      <div className="form-group">
                        <label>End Date:</label>
                        <input
                          type="date"
                          value={finalizeData.end_date}
                          onChange={(e) => handleFinalizeDataChange('end_date', e.target.value)}
                          className="date-input"
                        />
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label>Location:</label>
                      <div className="location-display">
                        {finalizeData.location || 'Not specified'}
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>Scholarship Percentage:</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={finalizeData.scholarship_percent}
                          onChange={(e) => handleFinalizeDataChange('scholarship_percent', parseInt(e.target.value) || 0)}
                          className="number-input"
                          placeholder="0"
                        />
                      </div>
                      <div className="form-group">
                        <label>Final Fee:</label>
                        <input
                          type="text"
                          value={finalizeData.final_fee}
                          onChange={(e) => handleFinalizeDataChange('final_fee', e.target.value)}
                          className="text-input"
                          placeholder="e.g., $400 per person, per month"
                        />
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>Onboarding Contact Name:</label>
                        <input
                          type="text"
                          value={finalizeData.onboarding_contact_name}
                          onChange={(e) => handleFinalizeDataChange('onboarding_contact_name', e.target.value)}
                          className="text-input"
                          placeholder="e.g., John Smith"
                        />
                      </div>
                      <div className="form-group">
                        <label>Onboarding Contact Email:</label>
                        <input
                          type="email"
                          value={finalizeData.onboarding_contact_email}
                          onChange={(e) => handleFinalizeDataChange('onboarding_contact_email', e.target.value)}
                          className="text-input"
                          placeholder="e.g., john@aethos.org"
                        />
                      </div>
                    </div>
                  </div>
                )}
                
              </div>
              
              <div className="modal-footer">
                <button onClick={closeFinalizeModal} className="cancel-btn">
                  Cancel
                </button>
                <button 
                  onClick={finalizeApplication} 
                  disabled={finalizing || !finalizeData.status} 
                  className="finalize-submit-btn"
                >
                  {finalizing ? 'Finalizing...' : 'Finalize'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationDetail;
